import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Brain, AlertTriangle, Shield, CheckCircle, XCircle, Info, Cpu, FileText, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface AIModel {
  name: string;
  provider: string;
  version: string;
  purpose: string;
  capabilities: string[];
  limitations: string[];
  dataUsage: string;
  trainingCutoff: string;
}

const aiModels: AIModel[] = [
  {
    name: "AI Therappi® (Therapeutic Agent)",
    provider: "Anthropic (Claude)",
    version: "claude-sonnet-4-5",
    purpose: "Provides empathetic, therapeutic-style conversations using evidence-based techniques like CBT, DBT, and mindfulness",
    capabilities: [
      "Empathetic conversational support",
      "Cognitive Behavioural Therapy (CBT) techniques",
      "Dialectical Behaviour Therapy (DBT) skills",
      "Mindfulness guidance",
      "Thought reframing exercises",
      "Crisis detection and safety resource referral"
    ],
    limitations: [
      "Cannot diagnose mental health conditions",
      "Cannot prescribe medication or treatment",
      "Cannot replace professional therapy",
      "May misunderstand cultural nuances",
      "Cannot provide emergency medical advice",
      "Limited to text-based interaction"
    ],
    dataUsage: "Conversations are processed in real-time and not used for model training. Zero data retention policy with Anthropic.",
    trainingCutoff: "Early 2025"
  },
  {
    name: "Dream Analyst",
    provider: "OpenAI (GPT-4)",
    version: "gpt-4-turbo",
    purpose: "Analyses dream descriptions to identify themes, symbols, and potential psychological insights",
    capabilities: [
      "Dream theme identification",
      "Symbol interpretation (Jungian approach)",
      "Pattern recognition across dreams",
      "Emotional tone analysis",
      "Narrative structure analysis"
    ],
    limitations: [
      "Interpretations are speculative, not diagnostic",
      "Cultural symbol meanings may vary",
      "Cannot access subconscious meaning",
      "Based on common psychological frameworks only",
      "No clinical validity claims"
    ],
    dataUsage: "Dream descriptions are processed via API. Data is not used for model training (API agreement in place).",
    trainingCutoff: "April 2024"
  },
  {
    name: "Guidance Interpreter",
    provider: "Google (Gemini)",
    version: "gemini-1.5-pro",
    purpose: "Provides analytical insights for tarot, oracle, and spiritual guidance tools",
    capabilities: [
      "Card spread interpretation",
      "Pattern synthesis across readings",
      "Reflective question generation",
      "Symbolic meaning explanation"
    ],
    limitations: [
      "For entertainment and reflection only",
      "No predictive or fortune-telling claims",
      "Cannot provide life advice with certainty",
      "Interpretations are suggestive, not prescriptive"
    ],
    dataUsage: "Reading data processed in real-time only. Not stored or used for training.",
    trainingCutoff: "November 2024"
  }
];

const AITransparency = () => {
  return (
    <>
      <Helmet>
        <title>AI Transparency | Therappi Model Card</title>
        <meta name="description" content="Detailed information about the AI models used in Therappi, including capabilities, limitations, and data usage policies. Full transparency about our AI systems." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Brain className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">AI Transparency</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We believe in complete transparency about our AI systems. This page provides detailed information 
              about the AI models powering Therappi, including their capabilities, limitations, and how your data is handled.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last updated: December 2024 | Version 1.0
            </p>
          </div>

          {/* Critical disclaimer */}
          <Alert variant="destructive" className="mb-8 border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-5 w-5" />
            <AlertTitle className="text-lg">Important Disclaimer</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="font-semibold mb-2">Therappi AI is NOT:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>A replacement for professional mental health treatment</li>
                <li>A medical device or diagnostic tool</li>
                <li>A crisis intervention service</li>
                <li>A substitute for licensed therapy or counselling</li>
              </ul>
              <p className="mt-3">
                If you're experiencing a mental health crisis, please contact emergency services or a crisis helpline immediately.
                Visit our <a href="/crisis" className="underline font-medium">Crisis Resources</a> page.
              </p>
            </AlertDescription>
          </Alert>

          {/* What is a Model Card */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                What is an AI Model Card?
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              <p>
                A Model Card is a standardised document that provides transparency about AI systems. 
                Inspired by research from Google and the AI ethics community, it helps users understand:
              </p>
              <ul className="list-disc list-inside mt-3 space-y-1">
                <li>What the AI model can and cannot do</li>
                <li>Known limitations and potential biases</li>
                <li>How data is collected, used, and protected</li>
                <li>Intended use cases and populations</li>
              </ul>
            </CardContent>
          </Card>

          {/* AI Models */}
          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
            <Cpu className="h-6 w-6 text-primary" />
            AI Models in Use
          </h2>

          <div className="space-y-6 mb-12">
            {aiModels.map((model) => (
              <Card key={model.name} className="overflow-hidden">
                <CardHeader className="bg-muted/30">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="text-xl">{model.name}</CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="outline">{model.provider}</Badge>
                      <Badge variant="secondary">{model.version}</Badge>
                    </div>
                  </div>
                  <CardDescription className="mt-2">{model.purpose}</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Capabilities */}
                    <div>
                      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Capabilities
                      </h4>
                      <ul className="space-y-2">
                        {model.capabilities.map((cap) => (
                          <li key={cap} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-green-500 mt-1">✓</span>
                            {cap}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Limitations */}
                    <div>
                      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-destructive" />
                        Limitations
                      </h4>
                      <ul className="space-y-2">
                        {model.limitations.map((lim) => (
                          <li key={lim} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-destructive mt-1">✗</span>
                            {lim}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-primary" />
                        Data Usage
                      </h4>
                      <p className="text-sm text-muted-foreground">{model.dataUsage}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <Info className="h-4 w-4 text-primary" />
                        Training Data Cutoff
                      </h4>
                      <p className="text-sm text-muted-foreground">{model.trainingCutoff}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Safety measures */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                AI Safety Measures
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Crisis Detection</h4>
                  <p className="text-sm text-muted-foreground">
                    All AI conversations are monitored for crisis indicators. When detected, the system 
                    immediately provides crisis resources and can activate Crisis Mode with professional support information.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Harmful Content Prevention</h4>
                  <p className="text-sm text-muted-foreground">
                    Our "Responsible Exploration Mode" detects potentially harmful requests (eating disorders, 
                    self-harm, dangerous wellness trends) and provides evidence-based education rather than enabling harm.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Human Oversight</h4>
                  <p className="text-sm text-muted-foreground">
                    Critical safety events are logged for clinical review. Our Clinical Lead reviews 
                    crisis activations monthly to improve safety protocols.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Scope Boundaries</h4>
                  <p className="text-sm text-muted-foreground">
                    AI agents are explicitly instructed not to diagnose, prescribe, or provide 
                    medical advice. They will redirect users to professionals when appropriate.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Intended users */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Intended Users & Populations
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Intended For:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Adults (18+) seeking self-reflection tools</li>
                    <li>Young people (13-17) with parental consent</li>
                    <li>Users seeking wellness support between therapy sessions</li>
                    <li>Individuals building emotional regulation skills</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Not Intended For:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Active crisis intervention</li>
                    <li>Replacement for professional treatment</li>
                    <li>Diagnosis of mental health conditions</li>
                    <li>Children under 13 (COPPA compliance)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedback */}
          <Card>
            <CardHeader>
              <CardTitle>Provide Feedback</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              <p>
                We continuously work to improve our AI systems. If you experience any issues, 
                inappropriate responses, or have suggestions, please contact us:
              </p>
              <ul className="list-disc list-inside mt-3 space-y-1">
                <li>Email: <a href="mailto:ai-feedback@therappi.app" className="text-primary hover:underline">ai-feedback@therappi.app</a></li>
                <li>Use the Safety Report feature within the app</li>
                <li>Contact your clinician if using Therappi through a healthcare provider</li>
              </ul>
            </CardContent>
          </Card>

          {/* Back link */}
          <div className="mt-8 text-center">
            <Button variant="outline" asChild>
              <a href="/">← Back to Home</a>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AITransparency;
